import { useCallback, useMemo, useState } from "react";

import { type AWSNodeConfigApiParameters, type CloudFlowNodeType } from "@doitintl/cmp-models";
import { Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";
import omit from "lodash/omit";

import { cloudflowTexts } from "../../../../../../assets/texts";
import { useErrorSnackbar } from "../../../../../../Components/SharedSnackbar/SharedSnackbar.context";
import { useNodeConfigurationContext } from "../../NodeConfigurationContext";
import { useErrorUpdates } from "../hooks";
import { useAWSWaiters } from "./useAWSWaiters";
import { WaiterConfigOverrides } from "./WaiterConfigOverrides";
import { WaiterParameters } from "./WaiterParameters";

export const WaiterConfig = () => {
  const showError = useErrorSnackbar();
  const { nodeConfig, updateNode } = useNodeConfigurationContext<CloudFlowNodeType.ACTION>();
  const parameters = nodeConfig.parameters as AWSNodeConfigApiParameters;
  const [enabled, setEnabled] = useState(Boolean(parameters.waiterConfiguration));
  const { waiters, waitersError } = useAWSWaiters(parameters.operation.service, parameters.operation.version);
  const selectedWaiter = useMemo(
    () => waiters?.find((waiter) => waiter.id === parameters.waiterConfiguration?.waiter) || null,
    [waiters, parameters.waiterConfiguration]
  );

  const [isWaiterValid, setIsWaiterValid] = useState(true);
  useErrorUpdates<CloudFlowNodeType.ACTION>({
    isValid: isWaiterValid,
    errorKey: "waiter_error",
    errorMessage: cloudflowTexts.STATUS.WAITER_ERROR,
  });

  const clearWaiterConfiguration = useCallback(() => {
    updateNode((prev) => omit(prev, "parameters.waiterConfiguration"));
    setIsWaiterValid(true);
  }, [updateNode, setIsWaiterValid]);

  if (waitersError) {
    showError("Failed to load waiters. Try again later.");
    return null;
  }

  if (!waiters || waiters.length === 0) {
    return null;
  }

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
        Waiter
      </Typography>

      <Typography variant="body2" color="text.secondary">
        Wait for this action to complete before continuing
      </Typography>

      <FormControlLabel
        control={
          <Checkbox
            name="enabled"
            checked={enabled}
            onChange={(e) => {
              const isEnabled = Boolean(e.target.checked);
              setEnabled(isEnabled);

              if (!isEnabled) {
                clearWaiterConfiguration();
              }
            }}
          />
        }
        label="Enable waiter"
      />

      {enabled && (
        <WaiterConfigOverrides
          parameters={parameters}
          waiters={waiters}
          selectedWaiter={selectedWaiter}
          setIsWaiterValid={setIsWaiterValid}
        />
      )}

      {enabled && <WaiterParameters parameters={parameters} selectedWaiter={selectedWaiter} key={selectedWaiter?.id} />}
    </Stack>
  );
};
